import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { colors } from '../../styles/colors';

const JOURS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function toYMD(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function parseYMD(str) {
  if (!str || typeof str !== 'string') return null;
  const d = new Date(str + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function formatDisplay(ymd) {
  const d = parseYMD(ymd);
  if (!d) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function firstWeekdayOfMonth(year, month) {
  const raw = new Date(year, month, 1).getDay(); // 0=Dim … 6=Sam
  return raw === 0 ? 6 : raw - 1; // lundi=0 … dimanche=6
}

const navBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  border: '1px solid rgba(255,255,255,0.4)',
  borderRadius: '0.25rem',
  backgroundColor: 'transparent',
  color: colors.text.white,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

/**
 * DatePicker — sélecteur de date custom aligné avec le design system.
 *
 * @param {string}   value         Date YYYY-MM-DD (mode contrôlé)
 * @param {string}   defaultValue  Date YYYY-MM-DD (mode non-contrôlé)
 * @param {function} onChange      (value: string) => void
 * @param {function} onBlur        (value: string) => void  — appelé à la fermeture
 * @param {function} onKeyDown     (e: Event) => void  — transmis au trigger
 * @param {string}   min           Date min YYYY-MM-DD
 * @param {string}   max           Date max YYYY-MM-DD
 * @param {boolean}  disabled
 * @param {boolean}  readOnly      Affiche la date en texte, sans interaction
 * @param {boolean}  autoFocus     Ouvre le calendrier au montage
 * @param {string}   placeholder
 * @param {object}   style         Styles supplémentaires pour le déclencheur
 * @param {'sm'|'md'} size
 * @param {string}   name          Pour le hidden input (soumission form)
 * @param {boolean}  required
 */
export function DatePicker({
  value,
  defaultValue,
  onChange,
  onBlur,
  onKeyDown,
  min,
  max,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  placeholder = 'JJ/MM/AAAA',
  style,
  size = 'md',
  name,
  required,
}) {
  const today = toYMD(new Date());

  // Contrôlé vs non-contrôlé
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const currentValue = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  // Navigation dans le calendrier
  const initialDate = parseYMD(currentValue) || new Date();
  const [navYear, setNavYear] = useState(initialDate.getFullYear());
  const [navMonth, setNavMonth] = useState(initialDate.getMonth());

  const containerRef = useRef(null);
  const listboxId = useId();

  // Ouvrir au montage si autoFocus
  useEffect(() => {
    if (autoFocus && !readOnly && !disabled) {
      setOpen(true);
      setFocused(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync navigation quand la valeur change
  useEffect(() => {
    const d = parseYMD(currentValue);
    if (d) {
      setNavYear(d.getFullYear());
      setNavMonth(d.getMonth());
    }
  }, [currentValue]);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        onBlur?.(currentValue);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, currentValue, onBlur]);

  const handleSelect = useCallback((ymd) => {
    if (!isControlled) setInternalValue(ymd);
    onChange?.(ymd);
    setOpen(false);
    setFocused(false);
    onBlur?.(ymd);
  }, [isControlled, onChange, onBlur]);

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setFocused(false);
      onBlur?.(currentValue);
    } else if (e.key === 'Enter') {
      if (open) {
        setOpen(false);
        setFocused(false);
        onBlur?.(currentValue);
      } else {
        setOpen(true);
        setFocused(true);
      }
    }
    onKeyDown?.(e);
  };

  const isDayDisabled = (ymd) => {
    if (min && ymd < min) return true;
    if (max && ymd > max) return true;
    return false;
  };

  // Grille du calendrier
  const daysInMonth = new Date(navYear, navMonth + 1, 0).getDate();
  const cells = [
    ...Array(firstWeekdayOfMonth(navYear, navMonth)).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const prevMonth = () => {
    if (navMonth === 0) { setNavYear(y => y - 1); setNavMonth(11); }
    else setNavMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (navMonth === 11) { setNavYear(y => y + 1); setNavMonth(0); }
    else setNavMonth(m => m + 1);
  };

  // Styles du déclencheur selon la taille
  const sizeMap = {
    sm: { padding: '0.25rem 0.5rem', fontSize: '0.8125rem', borderRadius: '0.25rem', gap: '0.375rem' },
    md: { padding: '0.5rem 0.75rem', fontSize: '0.95rem',   borderRadius: '0.375rem', gap: '0.5rem' },
  };
  const sz = sizeMap[size] || sizeMap.md;

  const triggerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${focused ? colors.primary.teal : colors.border.light}`,
    backgroundColor: disabled ? colors.background.lightGray : colors.background.input,
    cursor: (readOnly || disabled) ? 'default' : 'pointer',
    outline: 'none',
    userSelect: 'none',
    transition: 'border-color 0.15s',
    ...sz,
    // Les styles passés en prop écrasent les défauts (pour correspondre aux inputStyle existants)
    ...style,
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>

      {/* Déclencheur */}
      <div
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => {
          if (readOnly || disabled) return;
          setOpen(o => !o);
          setFocused(true);
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (!containerRef.current?.contains(e.relatedTarget)) {
            setFocused(false);
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        style={triggerStyle}
      >
        <span style={{ color: currentValue ? (style?.color || colors.text.primary) : colors.text.placeholder }}>
          {currentValue ? formatDisplay(currentValue) : placeholder}
        </span>
        {!readOnly && !disabled && (
          <Calendar size={size === 'sm' ? 13 : 15} color={colors.text.secondary} style={{ flexShrink: 0 }} />
        )}
      </div>

      {/* Input caché pour la soumission de formulaire */}
      {name && (
        <input type="hidden" name={name} value={currentValue} required={required} />
      )}

      {/* Calendrier déroulant */}
      {open && (
        <div
          id={listboxId}
          role="listbox"
          style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          zIndex: 9999,
          backgroundColor: colors.background.white,
          border: `2px solid ${colors.primary.teal}`,
          borderRadius: '0.5rem',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          minWidth: '270px',
        }}>

          {/* Navigation mois/année — bandeau teal */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: colors.primary.teal,
            padding: '0.6rem 0.75rem',
          }}>
            <button type="button" onClick={prevMonth} style={navBtnStyle}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontWeight: '700', fontSize: '1rem', color: colors.text.white }}>
              {MOIS_NOMS[navMonth]} {navYear}
            </span>
            <button type="button" onClick={nextMonth} style={navBtnStyle}>
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Grille jours + en-têtes */}
          <div style={{ padding: '0.75rem' }}>

            {/* En-têtes des jours */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
              {JOURS.map(j => (
                <div key={j} style={{
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: colors.text.secondary,
                  padding: '0.2rem 0',
                }}>
                  {j}
                </div>
              ))}
            </div>

            {/* Jours */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {cells.map((day, i) => {
                if (!day) return <div key={`e-${i}`} />;
                const ymd = `${navYear}-${String(navMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isSelected = ymd === currentValue;
                const isToday    = ymd === today;
                const isOff      = isDayDisabled(ymd);
                return (
                  <button
                    key={ymd}
                    type="button"
                    disabled={isOff}
                    onClick={() => handleSelect(ymd)}
                    style={{
                      padding: '0.35rem 0',
                      textAlign: 'center',
                      fontSize: '0.9rem',
                      borderRadius: '0.25rem',
                      border: isToday && !isSelected
                        ? `1px solid ${colors.primary.teal}`
                        : '1px solid transparent',
                      backgroundColor: isSelected ? colors.primary.teal : 'transparent',
                      color: isSelected
                        ? colors.text.white
                        : isOff
                          ? colors.text.placeholder
                          : colors.text.primary,
                      fontWeight: isToday ? '700' : '400',
                      cursor: isOff ? 'not-allowed' : 'pointer',
                      opacity: isOff ? 0.4 : 1,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => {
                      if (!isOff && !isSelected) e.currentTarget.style.backgroundColor = colors.background.lightGray;
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Raccourci Aujourd'hui */}
            {!isDayDisabled(today) && (
              <div style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: `1px solid ${colors.border.light}`,
                textAlign: 'center',
              }}>
                <button
                  type="button"
                  onClick={() => handleSelect(today)}
                  style={{
                    fontSize: '0.875rem',
                    color: colors.primary.teal,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  Aujourd'hui
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
